/**
 * Covermatic Studio task queue.
 */
(() => {
  const runtime = window.CovermaticRuntime || window.AlphaRuntime || {};
  window.CovermaticRuntime = runtime;
  window.AlphaRuntime = runtime;
  const { Config, Utils, ApiClient, Events, StorageKeys } = runtime;

  /**
   * @typedef {Object} AlphaTemplateSelection
   * @property {string|null} id
   * @property {string} title
   * @property {string} imageUrl
   * @property {string|null} base64
   * @property {string|null} handle
   * @property {'premade'|'style'} selectionType
   * @property {'premade'|'style-asset'|'style-custom'} selectionMode
   * @property {string|null} styleId
   * @property {number|null} styleAssetIndex
   * @property {string|null} styleAssetId
   * @property {string|null} styleAssetTitle
   * @property {string|null} systemPrompt
   */

  /**
   * @typedef {Object} AlphaTaskPayload
   * @property {string=} type
   * @property {string=} title
   * @property {string=} artist
   * @property {string=} concept
   * @property {number=} qty
   * @property {string=} editRequest
   * @property {string=} imageUrl
   * @property {number|string=} sourceCoverArtId
   * @property {string=} sourceFileName
   * @property {string=} logoFreeSourceUrl
   * @property {string=} finalDetails
   * @property {AlphaTemplateSelection|null=} template
   * @property {string|null=} templateInput
   * @property {string[]|null=} photoInputs
   */

  /**
   * @typedef {Object} AlphaTask
   * @property {string} id
   * @property {'pending'|'running'|'completed'|'failed'} status
   * @property {number} progress
   * @property {AlphaTaskPayload} data
   * @property {number} createdAt
   * @property {string=} pollJobId
   * @property {number=} pollStartedAt
   * @property {number=} networkErrorCount
   * @property {boolean=} awaitingServerJob
   * @property {string=} error
   * @property {number=} _timer
   */

  const TAB_ID = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  const POLL_MS = 3000;
  const POLL_MAX_MS = 10 * 60 * 1000;
  const NETWORK_RETRY_LIMIT = 8;
  const POLL_LEASE_MS = 12000;
  const POLL_LEASE_PREFIX = `cm_task_poll_lease:${Config.storageNamespace || 'covermatic:cover_art'}:`;

  const TaskManager = {
    tasks: new Map(),
    queue: [],
    activeCount: 0,
    maxConcurrent: 3,
    activePollsByJobId: new Map(),
    channel: null,

    init() {
      this.initBroadcast();
      this.load();
      this.resumePersistedTasks();

      document.addEventListener('click', (event) => {
        const btn = event.target.closest('.remove-task');
        if (btn?.dataset.taskId) this.removeTask(btn.dataset.taskId);
      });

      [Utils.$('clearTasks'), Utils.$('clearTasksModal'), Utils.$('#clear-tasks-creative')].forEach((btn) => {
        if (!btn) return;
        btn.addEventListener('click', (event) => {
          event.preventDefault();
          this.clearCompleted();
        });
      });

      window.addEventListener('pagehide', () => this.releaseOwnedPollLeases());

      this.processQueue();
    },

    initBroadcast() {
      if (typeof BroadcastChannel !== 'function') return;
      this.channel = new BroadcastChannel(`covermatic_tasks:${Config.storageNamespace || 'covermatic:cover_art'}`);
      this.channel.onmessage = (event) => {
        const message = event?.data || {};
        if (message.source === TAB_ID || message.type !== 'task:update' || !message.task?.id) return;
        const existing = this.tasks.get(message.task.id);
        if (!existing || Number(message.task.createdAt || 0) >= Number(existing.createdAt || 0)) {
          this.tasks.set(message.task.id, { ...existing, ...message.task });
          if (message.task.status === 'completed' || message.task.status === 'failed') {
            this.releasePollLease(message.task.pollJobId);
            const timer = this.activePollsByJobId.get(message.task.pollJobId);
            if (timer) window.clearTimeout(timer);
            this.activePollsByJobId.delete(message.task.pollJobId);
          }
          this.save();
          this.render();
        }
      };
    },

    broadcastTask(task) {
      if (!this.channel || !task) return;
      const { _timer, ...safeTask } = task;
      this.channel.postMessage({ type: 'task:update', source: TAB_ID, task: safeTask });
    },

    resumePersistedTasks() {
      this.activeCount = 0;

      for (const [id, task] of this.tasks.entries()) {
        const hasPollJobId = typeof task.pollJobId === 'string' && task.pollJobId.trim();
        const isRecoverable = task.status === 'pending' || task.status === 'running';

        if (!isRecoverable) continue;

        if (hasPollJobId) {
          task.status = 'running';
          task.progress = Math.max(Number(task.progress || 0), 5);
          task.pollStartedAt = Number(task.pollStartedAt || task.createdAt || Date.now());
          this.activeCount += 1;
          this.poll(task);
          continue;
        }

        if (!this.queue.includes(id)) {
          task.status = 'pending';
          this.queue.push(id);
        }
      }

      this.save();
      this.render();
    },

    /**
     * @param {AlphaTaskPayload} data
     * @returns {AlphaTask}
     */
    createTask(data) {
      const id = crypto.randomUUID();
      /** @type {AlphaTask} */
      const task = { id, status: 'pending', progress: 0, data, createdAt: Date.now() };
      this.tasks.set(id, task);
      this.queue.push(id);
      this.save();
      this.render();
      this.processQueue();
      Events.emit('task:created', { task });
      return task;
    },

    async processQueue() {
      while (this.queue.length > 0 && this.activeCount < this.maxConcurrent) {
        const id = this.queue.shift();
        const task = this.tasks.get(id);
        if (task) this.runTask(task);
      }
    },

    /**
     * @param {AlphaTask} task
     */
    async runTask(task) {
      this.activeCount += 1;
      task.status = 'running';
      task.pollJobId = task.pollJobId || crypto.randomUUID();
      task.pollStartedAt = task.pollStartedAt || Date.now();
      task.networkErrorCount = 0;
      this.save();
      this.render();

      try {
        const isEdit = task.data.type === 'edit' || task.data.type === 'creative-edit';
        const applyConfirmedJobId = (result) => {
          const confirmedJobId = String(result?.jobId || '').trim();
          if (confirmedJobId && confirmedJobId !== task.pollJobId) {
            task.pollJobId = confirmedJobId;
            this.save();
          }
          if (task.data?.type === 'free-cover-art') {
            task.data.freeQuotaSource = String(result?.quotaSource || task.data.freeQuotaSource || '').trim();
            task.data.applyFreeWatermark = result?.applyFreeWatermark === true || task.data.applyFreeWatermark === true;
          }
        };

        const body = {
          jobId: task.pollJobId,
          customerId: Config.customerId,
          toolSlug: task.data.toolSlug || Config.toolSlug,
          nicheSlug: task.data.nicheSlug || Config.nicheSlug,
          assetFamily: task.data.assetFamily || Config.assetFamily,
          assetType: task.data.assetType || Config.assetType,
          styleSlug: task.data.styleSlug || '',
          styleName: task.data.styleName || '',
          canvasReady: task.data.canvasReady === true,
          sizePreset: task.data.sizePreset || Config.sizePreset || 'square_1_1',
          imageAspectRatio: task.data.imageAspectRatio || Config.imageAspectRatio || '1:1',
        };
        if (isEdit) {
          const editEndpoint = this.resolveEditEndpoint(task.data);
          Object.assign(body, {
            editRequest: task.data.editRequest,
            imageUrl: task.data.imageUrl,
            sourceFileName: task.data.sourceFileName,
            templateInput: task.data.templateInput,
            photoInputs: task.data.photoInputs,
            assetInputs: task.data.assetInputs,
          });
          applyConfirmedJobId(await ApiClient.submitJob(editEndpoint, body));
        } else {
          const generateEndpoint = this.resolveGenerateEndpoint(task.data);
          if (task.data.type === 'creative-lab') {
            Object.assign(body, {
              creativeBrief: task.data.concept.trim(),
              assetType: task.data.assetType,
              sizePreset: task.data.sizePreset,
              assetInputs: task.data.assetInputs,
              systemPrompt: task.data.systemPrompt || '',
              promoDirection: task.data.promoDirection || null,
            });
          } else if (task.data.type === 'make-official') {
            Object.assign(body, {
              coverArtId: task.data.sourceCoverArtId,
              fileName: task.data.sourceFileName,
              sourceFileName: task.data.sourceFileName,
              imageUrl: task.data.imageUrl,
              logoFreeSourceUrl: task.data.logoFreeSourceUrl,
              finalDetails: task.data.finalDetails,
              artistName: task.data.artist,
              songName: task.data.title,
            });
          } else {
            Object.assign(body, {
              aiType: 'nanopro',
              artistName: task.data.artist,
              songName: task.data.title,
              coverArtIdea: task.data.concept.trim(),
              numImg: task.data.qty,
              templateInput: task.data.templateInput,
              photoInputs: task.data.photoInputs,
            });
          }
          applyConfirmedJobId(await ApiClient.submitJob(generateEndpoint, body));
        }

        this.poll(task);
      } catch (error) {
        if (this.isTransientFetchError(error) && task.pollJobId) {
          task.awaitingServerJob = true;
          task.networkErrorCount = Number(task.networkErrorCount || 0) + 1;
          task.error = 'Connection interrupted after submit, still checking...';
          this.save();
          this.render();
          this.poll(task);
          return;
        }
        if (task.pollJobId) this.releasePollLease(task.pollJobId);
        task.status = 'failed';
        task.error = this.normalizeTaskError(error);
        Utils.toast(task.error, 'error');
        if (task.data?.type === 'make-official' && String(task.error || '').toLowerCase().includes('credit')) {
          Events.emit('modal:buy-open-requested');
        }
        this.activeCount = Math.max(0, this.activeCount - 1);
        this.save();
        this.render();
        this.broadcastTask(task);
        Events.emit('task:failed', { task, error });
      }
    },

    /**
     * @param {AlphaTask} task
     */
    poll(task) {
      const jobId = String(task.pollJobId || '').trim();
      if (!jobId) return;
      if (this.activePollsByJobId.has(jobId)) return;
      if (!this.acquirePollLease(jobId, { takeover: true })) {
        task.error = '';
        this.save();
        this.render();
        const retryTimer = window.setTimeout(() => {
          this.activePollsByJobId.delete(jobId);
          this.poll(task);
        }, 1500);
        task._timer = retryTimer;
        this.activePollsByJobId.set(jobId, retryTimer);
        return;
      }

      task.pollStartedAt = Number(task.pollStartedAt || Date.now());
      task.networkErrorCount = Number(task.networkErrorCount || 0);

      const finish = (status, error = null, data = null) => {
        const timer = this.activePollsByJobId.get(jobId);
        if (timer) window.clearTimeout(timer);
        this.activePollsByJobId.delete(jobId);
        this.releasePollLease(jobId);

        task.status = status;
        if (error) task.error = this.normalizeTaskError(error);
        if (status === 'completed') {
          task.progress = 100;
          task.error = '';
          this.handleCompletion(task, this.normalizeCompletedImages(data));
        }

        this.activeCount = Math.max(0, this.activeCount - 1);
        this.save();
        this.render();
        this.broadcastTask(task);
        this.processQueue();

        if (status === 'failed') {
          Events.emit('task:failed', { task, error: error || new Error(task.error || 'Task failed') });
        }
      };

      const scheduleNext = (delay = POLL_MS) => {
        const timer = window.setTimeout(tick, delay);
        task._timer = timer;
        this.activePollsByJobId.set(jobId, timer);
      };

      const tick = async () => {
        if (!this.acquirePollLease(jobId, { takeover: true })) {
          this.activePollsByJobId.delete(jobId);
          task.error = '';
          this.save();
          this.render();
          scheduleNext(1500);
          return;
        }

        const elapsed = Date.now() - Number(task.pollStartedAt || Date.now());
        if (elapsed >= POLL_MAX_MS) {
          finish('failed', new Error('Timed out'));
          return;
        }

        try {
          if (task.progress < 95) task.progress = Math.min(95, 5 + (elapsed / POLL_MAX_MS) * 90);
          this.render();

          const res = await ApiClient.checkJob(task.pollJobId);
          if (res.status >= 500) {
            task.networkErrorCount += 1;
            task.error = 'Service is busy, still checking...';
            this.save();
            this.render();
            if (task.networkErrorCount > NETWORK_RETRY_LIMIT) {
              finish('failed', new Error('Service temporarily unavailable. Please refresh and check your gallery.'));
              return;
            }
            scheduleNext(Math.min(15000, POLL_MS * task.networkErrorCount));
            return;
          }
          if (res.status === 404 && task.awaitingServerJob) {
            task.networkErrorCount += 1;
            task.error = 'Waiting for the server to confirm this job...';
            this.save();
            this.render();
            if (task.networkErrorCount <= NETWORK_RETRY_LIMIT) {
              scheduleNext(Math.min(15000, POLL_MS * task.networkErrorCount));
              return;
            }
          }
          if (res.status >= 400 && res.status < 500) {
            throw new Error(res.status === 403 ? 'Insufficient credits' : 'Request failed');
          }

          const text = await res.text();
          let data;
          try {
            data = JSON.parse(text);
          } catch (error) {
            task.networkErrorCount += 1;
            scheduleNext(POLL_MS);
            return;
          }

          task.networkErrorCount = 0;
          task.awaitingServerJob = false;
          task.error = '';
          if (!data || data.status === 'pending') {
            this.save();
            scheduleNext(POLL_MS);
            return;
          }

          if (data.status === 'failed') throw new Error(data.error || 'Generation failed');

          finish('completed', null, data);
        } catch (error) {
          if (this.isTransientFetchError(error)) {
            task.networkErrorCount += 1;
            task.error = `Connection interrupted, still checking (${task.networkErrorCount}/${NETWORK_RETRY_LIMIT})...`;
            this.save();
            this.render();
            if (task.networkErrorCount <= NETWORK_RETRY_LIMIT) {
              scheduleNext(Math.min(15000, POLL_MS * task.networkErrorCount));
              return;
            }
          }
          finish('failed', error);
        }
      };

      scheduleNext(250);
    },

    acquirePollLease(jobId, options = {}) {
      if (!jobId) return false;
      const key = `${POLL_LEASE_PREFIX}${jobId}`;
      const now = Date.now();
      try {
        const current = JSON.parse(window.localStorage.getItem(key) || 'null');
        if (
          current?.owner &&
          current.owner !== TAB_ID &&
          Number(current.expiresAt || 0) > now &&
          !options.takeover
        ) {
          return false;
        }
        window.localStorage.setItem(key, JSON.stringify({ owner: TAB_ID, expiresAt: now + POLL_LEASE_MS }));
        return true;
      } catch (error) {
        return true;
      }
    },

    releasePollLease(jobId) {
      if (!jobId) return;
      const key = `${POLL_LEASE_PREFIX}${jobId}`;
      try {
        const current = JSON.parse(window.localStorage.getItem(key) || 'null');
        if (!current?.owner || current.owner === TAB_ID) window.localStorage.removeItem(key);
      } catch (error) {
        window.localStorage.removeItem(key);
      }
    },

    releaseOwnedPollLeases() {
      for (const jobId of this.activePollsByJobId.keys()) {
        this.releasePollLease(jobId);
      }
    },

    isTransientFetchError(error) {
      const message = String(error?.message || '').toLowerCase();
      return (
        error?.name === 'AbortError' ||
        message.includes('failed to fetch') ||
        message.includes('network') ||
        message.includes('load failed') ||
        message.includes('timeout')
      );
    },

    normalizeTaskError(error) {
      const message = String(error?.message || error || '').trim();
      if (!message) return 'Request failed. Please try again.';
      if (message.toLowerCase().includes('failed to fetch')) {
        return 'Connection interrupted. Your job may still finish; refresh and check your gallery.';
      }
      return message;
    },

    normalizeCompletedImages(data) {
      if (!data) return [];
      const candidates = [
        data.images,
        data.urls,
        data.result_urls,
        data.resultUrls,
        data.image ? [data.image] : null,
        data.url ? [data.url] : null,
        data.imageUrl ? [data.imageUrl] : null,
        data.image_url ? [data.image_url] : null,
      ];
      for (const candidate of candidates) {
        const list = Array.isArray(candidate) ? candidate : (candidate ? [candidate] : []);
        const usable = list.filter((item) => {
          if (!item) return false;
          if (typeof item === 'string') return item.trim();
          if (typeof item === 'object') {
            return item.url || item.URL || item.imageUrl || item.image_url || item.src;
          }
          return false;
        });
        if (usable.length) return usable;
      }
      return [];
    },

    /**
     * @param {AlphaTask} task
     * @param {Array<{url?: string}|string>|{url?: string}|string|null|undefined} images
     */
    handleCompletion(task, images) {
      const resultImages = Array.isArray(images) ? images : (images ? [images] : []);
      const isEdit = task.data.type === 'edit' || task.data.type === 'creative-edit';
      const fallbackDelta = isEdit ? Config.creditsEdit : Config.creditsGen;
      const completedImages = resultImages.map((image, index) => this.decorateCompletedImage(task, image, index));

      Events.emit('credits:refresh-requested', { fallbackDelta });
      if (task.data?.type === 'free-cover-art') Events.emit('free-generation:refresh-requested');

      if (isEdit && completedImages[0]) {
        Events.emit('gallery:apply-edit', { image: completedImages[0] });
      } else if (completedImages.length > 0) {
        Events.emit('gallery:prepend-images', { images: completedImages });
      } else {
        [1500, 6000, 15000].forEach((delay) => {
          window.setTimeout(() => {
            Events.emit('gallery:refresh-requested', { reason: 'task-completed-empty-images', taskId: task.id, pollJobId: task.pollJobId });
          }, delay);
        });
      }

      Events.emit('task:completed', { task, images: completedImages });
      const completedToolSlug = String(task.data?.toolSlug || Config.toolSlug || '').trim();
      if (completedToolSlug === 'photomatic') {
        [1500, 6000, 15000].forEach((delay) => {
          window.setTimeout(() => {
            Events.emit('gallery:refresh-requested', { reason: 'photomatic-task-completed', taskId: task.id });
          }, delay);
        });
      }
      Utils.toast('Task complete');
    },

    pendingCardId(taskId, index = 0) {
      return `${String(taskId || '').trim()}::${Number(index) || 0}`;
    },

    decorateCompletedImage(task, image, index = 0) {
      const sizePreset = String(task.data.sizePreset || 'square_1_1').trim() || 'square_1_1';
      const pendingTaskId = this.pendingCardId(task.id, index);
      if (image && typeof image === 'object') {
        return {
          ...image,
          size_preset: image.size_preset || image.sizePreset || sizePreset,
          is_free_generation: task.data?.type === 'make-official' ? false : (image.is_free_generation || task.data?.type === 'free-cover-art'),
          has_free_watermark: task.data?.type === 'make-official' ? false : (image.has_free_watermark || task.data?.applyFreeWatermark === true),
          free_quota_source: image.free_quota_source || task.data?.freeQuotaSource || '',
          conversion_type: image.conversion_type || (task.data?.type === 'make-official' ? 'make_official' : ''),
          officialized_from_cover_art_id: image.officialized_from_cover_art_id || task.data?.sourceCoverArtId || null,
          pendingTaskId,
          pending_task_id: pendingTaskId,
          tool_slug: image.tool_slug || task.data?.toolSlug || Config.toolSlug,
          niche_slug: image.niche_slug || task.data?.nicheSlug || Config.nicheSlug,
          asset_family: image.asset_family || task.data?.assetFamily || Config.assetFamily,
          asset_type: image.asset_type || task.data?.assetType || Config.assetType,
          style_slug: image.style_slug || task.data?.styleSlug || '',
          style_name: image.style_name || task.data?.styleName || '',
          canvas_ready: image.canvas_ready === true || task.data?.canvasReady === true,
        };
      }
      return {
        url: image,
        size_preset: sizePreset,
        is_free_generation: task.data?.type === 'free-cover-art',
        has_free_watermark: task.data?.type === 'make-official' ? false : task.data?.applyFreeWatermark === true,
        free_quota_source: task.data?.freeQuotaSource || '',
        conversion_type: task.data?.type === 'make-official' ? 'make_official' : '',
        officialized_from_cover_art_id: task.data?.sourceCoverArtId || null,
        pendingTaskId,
        pending_task_id: pendingTaskId,
        tool_slug: task.data?.toolSlug || Config.toolSlug,
        niche_slug: task.data?.nicheSlug || Config.nicheSlug,
        asset_family: task.data?.assetFamily || Config.assetFamily,
        asset_type: task.data?.assetType || Config.assetType,
        style_slug: task.data?.styleSlug || '',
        style_name: task.data?.styleName || '',
        canvas_ready: task.data?.canvasReady === true,
      };
    },

    removeTask(id) {
      const task = this.tasks.get(id);
      if (task?._timer) window.clearInterval(task._timer);
      if (task?._timer) window.clearTimeout(task._timer);
      if (task?.pollJobId) {
        this.activePollsByJobId.delete(task.pollJobId);
        this.releasePollLease(task.pollJobId);
      }
      this.tasks.delete(id);
      this.queue = this.queue.filter((queueId) => queueId !== id);
      this.save();
      this.render();
      Events.emit('task:removed', { id });
    },

    clearCompleted() {
      let changed = false;

      for (const [id, task] of this.tasks) {
        if (task.status !== 'completed' && task.status !== 'failed') continue;
        if (task._timer) window.clearInterval(task._timer);
        if (task._timer) window.clearTimeout(task._timer);
        if (task.pollJobId) {
          this.activePollsByJobId.delete(task.pollJobId);
          this.releasePollLease(task.pollJobId);
        }
        this.tasks.delete(id);
        this.queue = this.queue.filter((queueId) => queueId !== id);
        changed = true;
      }

      if (!changed) return;
      this.save();
      this.render();
      Events.emit('task:cleared');
    },

    save() {
      const entries = Array.from(this.tasks.entries()).map(([key, value]) => {
        const { _timer, ...rest } = value;
        return [key, rest];
      });
      Utils.writeStorage(StorageKeys.tasks, JSON.stringify({ tasks: entries, queue: this.queue }));
    },

    load() {
      try {
        const stored = JSON.parse(Utils.readStorage(StorageKeys.tasks, 'null'));
        if (!stored) return;

        stored.tasks.forEach(([key, value]) => {
          this.tasks.set(key, value);
        });
        this.queue = stored.queue.filter((id) => this.tasks.has(id));
      } catch (error) {
        console.warn('Failed to restore Covermatic tasks', error);
      }
    },

    render() {
      const lists = [Utils.$('taskList'), Utils.$('taskListModal'), Utils.$('#task-list-creative')];
      const counters = [Utils.$('taskCount'), Utils.$('taskCountModal'), Utils.$('#task-count-creative')];
      const containers = [Utils.$('taskManager'), Utils.$('taskManagerModal'), Utils.$('#task-manager-creative')];
      const failedTasks = Array.from(this.tasks.values())
        .filter((task) => task.status === 'failed')
        .sort((a, b) => b.createdAt - a.createdAt);
      const listHtml = failedTasks.map((task) => this.renderItem(task)).join('');

      lists.forEach((el) => { if (el) el.innerHTML = listHtml; });
      counters.forEach((el) => { if (el) el.textContent = failedTasks.length; });
      containers.forEach((el) => { if (el) el.classList.toggle('hidden', failedTasks.length === 0); });
    },

    resolveGenerateEndpoint(taskData) {
      if (taskData?.type === 'make-official') return 'officialize-free-cover';
      if (taskData?.type === 'free-cover-art') return 'free-generate';
      if (taskData?.type === 'creative-lab') return 'creative-lab-generate';
      const template = taskData?.template;
      const styleId = String(template?.styleId || '').trim().toLowerCase();
      if (template?.selectionType === 'style' && template?.selectionMode === 'style-custom' && styleId === 'dopehause') {
        return 'dopehause-generate';
      }
      return 'generate';
    },

    resolveEditEndpoint(taskData) {
      if (taskData?.type === 'creative-edit') return 'creative-lab-edit';
      return 'edit';
    },

    formatTaskDisplayName(taskData) {
      const artist = String(taskData?.artist || '').trim();
      const title = String(taskData?.title || '').trim();
      if (taskData?.type === 'creative-lab' || taskData?.type === 'creative-edit') {
        return String(taskData?.assetTypeLabel || taskData?.assetType || 'Creative Lab').trim();
      }
      if (taskData?.styleName && Config.toolSlug === 'photomatic') return String(taskData.styleName).trim();
      if (artist && title) return `${artist} - ${title}`;
      return artist || title || (Config.toolSlug === 'photomatic' ? 'Family portrait' : 'Untitled');
    },

    renderItem(task) {
      const displayName = this.formatTaskDisplayName(task.data);
      const message = String(task.error || 'Generation failed. Credits refunded.').trim();
      return `<div class="covermatic-task-failure" role="status">
        <div class="covermatic-task-failure__body">
          <strong>Generation failed. Credits refunded.</strong>
          <span>${Utils.escapeHtml(displayName)}</span>
          ${message && message !== 'Generation failed. Credits refunded.' ? `<small>${Utils.escapeHtml(message)}</small>` : ''}
        </div>
        <button class="remove-task covermatic-task-failure__close" type="button" data-task-id="${task.id}" aria-label="Dismiss failed generation">&times;</button>
      </div>`;
    },
  };

  runtime.TaskManager = TaskManager;
})();


